/**
 * 客户类型UI差异化渲染框架 - 组件工厂
 * 根据客户类型元数据配置动态渲染不同UI组件
 */

import { defineComponent, h, resolveComponent } from 'vue';

/**
 * 条件渲染的表达式解析
 * @param {Object} condition 条件表达式
 * @param {Object} data 数据对象
 * @returns {boolean} 条件是否满足
 */
export function evaluateCondition(condition, data) {
  if (!condition) return true;
  
  // 处理字段条件，如 { field: "extensions.isApiManufacturer", value: true }
  if (condition.field && condition.value !== undefined) {
    const fieldValue = getNestedValue(data, condition.field);
    return fieldValue === condition.value;
  }
  
  // 处理复杂条件，如 { operator: "and", conditions: [...] }
  if (condition.operator && condition.conditions) {
    if (condition.operator === 'and') {
      return condition.conditions.every(c => evaluateCondition(c, data));
    } else if (condition.operator === 'or') {
      return condition.conditions.some(c => evaluateCondition(c, data));
    }
  }
  
  return false;
}

/**
 * 获取嵌套对象的属性值
 * @param {Object} obj 对象
 * @param {string} path 属性路径，如"a.b.c"
 * @returns {any} 属性值
 */
export function getNestedValue(obj, path) {
  return path.split('.').reduce((prev, curr) => {
    return prev && prev[curr] !== undefined ? prev[curr] : undefined;
  }, obj);
}

/**
 * 渲染组件
 * @param {Object} params 组件参数
 * @param {Object} data 数据对象
 * @returns {VNode|null} 渲染的组件
 */
export function renderComponent(params, data) {
  if (!params || !params.type) {
    console.warn('无效的组件参数:', params);
    return null;
  }
  
  // 组件条件渲染判断
  if (params.condition && !evaluateCondition(params.condition, data)) {
    return null;
  }
  
  // 根据组件类型进行渲染
  const componentTypeMap = {
    'descriptive-list': renderDescriptiveList,
    'status-card': renderStatusCard,
    'score-card': renderScoreCard,
    'table': renderTable,
    'chart': renderChart,
    'statistic': renderStatistic,
    'action-list': renderActionList,
    'timeline': renderTimeline,
  };
  
  const renderFunction = componentTypeMap[params.type];
  if (!renderFunction) {
    console.warn(`未知的组件类型: ${params.type}`);
    return null;
  }
  
  return renderFunction(params, data);
}

/**
 * 动态组件 - 根据配置和条件渲染组件
 */
export const DynamicComponent = defineComponent({
  name: 'DynamicComponent',
  props: {
    config: {
      type: Object,
      required: true
    },
    data: {
      type: Object,
      required: true
    }
  },
  render() {
    const { config, data } = this;
    return renderComponent(config, data);
  }
});

/**
 * 动态Tab内容 - 渲染一组组件
 */
export const DynamicTabContent = defineComponent({
  name: 'DynamicTabContent',
  props: {
    tabConfig: {
      type: Object,
      required: true
    },
    customerData: {
      type: Object,
      required: true
    }
  },
  render() {
    const { tabConfig, customerData } = this;
    const Row = resolveComponent('a-row');
    const Col = resolveComponent('a-col');
    
    if (!tabConfig || !tabConfig.components || !tabConfig.components.length) {
      return h('div', { class: 'empty-tab-content' }, '无可用配置');
    }
    
    return h('div', { class: 'dynamic-tab-content' }, [
      h(Row, { gutter: 16 }, {
        default: () => tabConfig.components
          .filter(comp => !comp.condition || evaluateCondition(comp.condition, customerData))
          .sort((a, b) => (a.layout?.order || 0) - (b.layout?.order || 0))
          .map(comp => 
            h(Col, { 
              span: comp.layout?.span || 24,
              key: comp.id || `comp-${comp.type}-${comp.dataSource}`
            }, {
              default: () => h(DynamicComponent, {
                config: comp,
                data: customerData
              })
            })
          )
      })
    ]);
  }
});

// 需要在这里实现其他渲染函数，如renderDescriptiveList, renderStatusCard等
// 这里只提供一个简化版的示例
function renderDescriptiveList(params, data) {
  // 简化实现
  const div = document.createElement('div');
  div.innerHTML = `<div class="descriptive-list">${params.title || '描述列表'}</div>`;
  return div;
}

function renderStatusCard(params, data) {
  // 简化实现
  const div = document.createElement('div');
  div.innerHTML = `<div class="status-card">${params.title || '状态卡片'}</div>`;
  return div;
}

function renderScoreCard(params, data) {
  // 简化实现
  const div = document.createElement('div');
  div.innerHTML = `<div class="score-card">${params.title || '评分卡片'}</div>`;
  return div;
}

function renderTable(params, data) {
  // 简化实现
  const div = document.createElement('div');
  div.innerHTML = `<div class="table">${params.title || '表格'}</div>`;
  return div;
}

function renderChart(params, data) {
  // 简化实现
  const div = document.createElement('div');
  div.innerHTML = `<div class="chart">${params.title || '图表'}</div>`;
  return div;
}

function renderStatistic(params, data) {
  // 简化实现
  const div = document.createElement('div');
  div.innerHTML = `<div class="statistic">${params.title || '统计数据'}</div>`;
  return div;
}

function renderActionList(params, data) {
  // 简化实现
  const div = document.createElement('div');
  div.innerHTML = `<div class="action-list">${params.title || '操作列表'}</div>`;
  return div;
}

function renderTimeline(params, data) {
  // 简化实现
  const div = document.createElement('div');
  div.innerHTML = `<div class="timeline">${params.title || '时间线'}</div>`;
  return div;
} 